import React from 'react';
import { SideBar } from '../../sidebar';
import HeaderBanner from '../../../common/HeaderBanner';
import '../../../../styles/components/create-group.scss';
import i18n from '../../../../i18n';
import { Expand, Shrink, Plus, Trash2, Edit2, Search } from 'lucide-react';
import { getAttributes, deleteAttribute } from '../../../../models/attributes';
import { getAllCategories } from '../../../../models/categories';
import swal from 'sweetalert';
import { Spinner } from 'react-bootstrap';

export class AttributesList extends React.Component {
  constructor() {
    super();
    this.state = {
      attributes: [],
      filteredAttributes: [],
      categories: [],
      is_loading: false,
      hideSidebar: false,
      searchTerm: '',
      filterCategory: '',
    };
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    this.loadCategories();
    this.loadAttributes();
  }

  async loadCategories() {
    try {
      const response = await getAllCategories();
      if (response && response.data) {
        this.setState({ categories: response.data });
      }
    } catch (err) {
      console.error('Error loading categories:', err);
    }
  }

  async loadAttributes() {
    this.setState({ is_loading: true });
    try {
      const response = await getAttributes();
      if (response && response.data) {
        this.setState({
          attributes: response.data,
          filteredAttributes: response.data,
        });
      }
    } catch (err) {
      console.error('Error loading attributes:', err);
    } finally {
      this.setState({ is_loading: false });
    }
  }

  handleSearch = (e) => {
    const searchTerm = e.target.value;
    this.setState({ searchTerm }, this.applyFilters);
  };

  handleCategoryFilter = (e) => {
    this.setState({ filterCategory: e.target.value }, this.applyFilters);
  };

  applyFilters = () => {
    const { attributes, searchTerm, filterCategory } = this.state;
    let filtered = [...attributes];

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (a) =>
          a.name.toLowerCase().includes(term) ||
          (a.name_ar && a.name_ar.toLowerCase().includes(term))
      );
    }

    if (filterCategory) {
      filtered = filtered.filter((a) => String(a.category) === String(filterCategory));
    }

    this.setState({ filteredAttributes: filtered });
  };

  handleDelete = async (id, name) => {
    const confirm = await swal({
      title: 'Are you sure?',
      text: `Delete attribute "${name}"? This cannot be undone.`,
      icon: 'warning',
      buttons: ['Cancel', 'Delete'],
      dangerMode: true,
    });

    if (confirm) {
      try {
        const response = await deleteAttribute(id);
        if (response && response.success) {
          swal(i18n.t('success'), 'Attribute deleted', 'success', {
            buttons: false,
            timer: 1500,
          });
          this.loadAttributes();
        } else {
          swal('Failed', response.message || 'Could not delete attribute', 'error');
        }
      } catch (err) {
        swal('Failed', 'An error occurred', 'error');
      }
    }
  };

  /* Get category name by ID from loaded categories */
  getCategoryName = (categoryId) => {
    if (!categoryId) return null;
    const cat = this.state.categories.find((c) => String(c.id) === String(categoryId));
    if (!cat) return categoryId; // fallback to raw ID
    const isArabic = i18n.language === 'ar';
    return isArabic ? cat.category_name_ar || cat.category_name : cat.category_name;
  };

  /* Get unique category IDs used by attributes */
  getUsedCategoryIds = () => {
    const ids = new Set();
    this.state.attributes.forEach((a) => {
      if (a.category) ids.add(String(a.category));
    });
    return Array.from(ids);
  };

  render() {
    const { filteredAttributes, is_loading, categories } = this.state;
    const usedCategoryIds = this.getUsedCategoryIds();

    return (
      <React.Fragment>
        <HeaderBanner
          title={i18n.t('attributes') || 'Attributes'}
          subtitle={i18n.t('manage_attributes') || 'Manage Attributes'}
        />
        <section className="dashboard-section">
          <div className="container">
            <div className="row">
              <div className="col-xl-3 col-lg-12 col-md-12">
                {!this.state.hideSidebar && <SideBar />}
              </div>
              <div
                className={
                  this.state.hideSidebar
                    ? 'col-xl-12 col-lg-12 col-md-12'
                    : 'col-xl-9 col-lg-12 col-md-12'
                }
              >
                <div className="card">
                  <div className="card-header">
                    <div className="expand-dashboard">
                      <span
                        style={{ fontSize: '1rem', marginRight: '0.5rem', cursor: 'pointer' }}
                        onClick={() => this.setState({ hideSidebar: !this.state.hideSidebar })}
                      >
                        {this.state.hideSidebar ? (
                          <Shrink strokeWidth={1} />
                        ) : (
                          <Expand strokeWidth={1} />
                        )}
                      </span>
                    </div>
                    <div className="header-action">
                      <button
                        className="button1 button1-primary"
                        onClick={() => (window.location.href = '/attributes/create')}
                      >
                        <Plus size={18} />
                        <span>{i18n.t('create') || 'Create New'}</span>
                      </button>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="create-group-container">
                      {/* Filters */}
                      <div className="form-section">
                        <h2>{i18n.t('attributes') || 'Attributes'}</h2>
                        <div className="form-row">
                          <div className="form-field">
                            <label>{i18n.t('search') || 'Search'}</label>
                            <div style={{ position: 'relative' }}>
                              <Search
                                size={16}
                                style={{
                                  position: 'absolute',
                                  left: '12px',
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  color: '#9ca3af',
                                }}
                              />
                              <input
                                type="text"
                                placeholder="Search by name..."
                                value={this.state.searchTerm}
                                onChange={this.handleSearch}
                                style={{ paddingLeft: '36px' }}
                              />
                            </div>
                          </div>
                          <div className="form-field">
                            <label>{i18n.t('category') || 'Category'}</label>
                            <select
                              value={this.state.filterCategory}
                              onChange={this.handleCategoryFilter}
                            >
                              <option value="">All Categories</option>
                              {categories
                                .filter((cat) => usedCategoryIds.includes(String(cat.id)))
                                .map((cat) => (
                                  <option key={cat.id} value={cat.id}>
                                    {cat.category_name}
                                  </option>
                                ))}
                            </select>
                          </div>
                        </div>
                      </div>

                      {/* Loading */}
                      {is_loading && (
                        <div style={{ textAlign: 'center', padding: '3rem' }}>
                          <Spinner animation="border" />
                        </div>
                      )}

                      {/* List */}
                      {!is_loading && filteredAttributes.length > 0 && (
                        <div className="form-section">
                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                              gap: '12px',
                            }}
                          >
                            {filteredAttributes.map((attr) => (
                              <div
                                key={attr.id}
                                className="enquiry-item"
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '14px',
                                  marginBottom: 0,
                                }}
                              >
                                {/* Icon */}
                                <div
                                  style={{
                                    width: '44px',
                                    height: '44px',
                                    borderRadius: '10px',
                                    background: 'rgba(16, 185, 129, 0.1)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                  }}
                                >
                                  <i
                                    className={`fa ${attr.icon || 'fa-tag'}`}
                                    style={{ color: '#10b981', fontSize: '18px' }}
                                  />
                                </div>

                                {/* Info */}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div
                                    style={{
                                      fontWeight: 600,
                                      fontSize: '14px',
                                      color: '#1f2937',
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                    }}
                                  >
                                    {attr.name}
                                  </div>
                                  {attr.name_ar && (
                                    <div
                                      style={{
                                        fontSize: '13px',
                                        color: '#6b7280',
                                        direction: 'rtl',
                                      }}
                                    >
                                      {attr.name_ar}
                                    </div>
                                  )}
                                  {attr.category && (
                                    <span
                                      style={{
                                        display: 'inline-block',
                                        marginTop: '4px',
                                        fontSize: '11px',
                                        fontWeight: 500,
                                        color: '#10b981',
                                        background: '#f0fdf4',
                                        padding: '2px 8px',
                                        borderRadius: '4px',
                                      }}
                                    >
                                      {this.getCategoryName(attr.category)}
                                    </span>
                                  )}
                                </div>

                                {/* Actions */}
                                <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                                  <button
                                    type="button"
                                    className="preview-action-btn"
                                    title="Edit"
                                    onClick={() =>
                                      (window.location.href = `/attributes/edit/${attr.id}`)
                                    }
                                  >
                                    <Edit2 size={16} />
                                  </button>
                                  <button
                                    type="button"
                                    className="preview-action-btn"
                                    title="Delete"
                                    onClick={() => this.handleDelete(attr.id, attr.name)}
                                    style={{ color: '#ef4444' }}
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Empty */}
                      {!is_loading && filteredAttributes.length === 0 && (
                        <div className="form-section" style={{ textAlign: 'center', padding: '3rem' }}>
                          <p style={{ color: '#9ca3af', marginBottom: '16px' }}>
                            {this.state.searchTerm || this.state.filterCategory
                              ? 'No attributes match your search.'
                              : 'No attributes created yet.'}
                          </p>
                          {!this.state.searchTerm && !this.state.filterCategory && (
                            <button
                              className="button1 button1-primary"
                              onClick={() => (window.location.href = '/attributes/create')}
                            >
                              <Plus size={18} /> Create First Attribute
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </React.Fragment>
    );
  }
}